import { StreamlinesSession } from '../icons/StreamlinesSession';
import { StreamlinesRun } from '../icons/StreamlinesRun';
import { StreamlinesBicycle } from '../icons/StreamlinesBicycle';
import { StreamlinesRider } from '../icons/StreamlinesRider';
import { StreamlinesUser } from '../icons/StreamlinesUser';
import { StreamlinesAdmin } from '../icons/StreamlinesAdmin';
import { StreamlinesRoute } from '../icons/StreamlinesRoute';
import { StreamlinesReports } from '../icons/StreamlinesReports';
import { StreamlinesAdvanced } from '../icons/StreamlinesAdvanced';
import { StreamlinesStatistics } from '../icons/StreamlinesStatistics';
import CalibrationView from '../pages/CalibrationView/CalibrationView';
import SessionCalculatedData from '../pages/SessionView/SessionDetails/SessionCalculatedData/SessionCalculatedData';
import SessionList from '../pages/SessionList/SessionList';
import SessionView from '../pages/SessionView/SessionView';
import RunList from '../pages/RunList/RunList';
import RunView from '../pages/RunView/RunView';
import EquipmentList from '../pages/EquipmentList/EquipmentList';
import RunOverview from 'pages/RunView/RunDetails/RunDetails';
import RunPlotter from 'pages/RunView/RunPlotter/RunPlotter';
import RunAuxSensor from 'pages/RunView/RunAuxSensor/RunAuxSensor';
import TeamsAdmin from '../pages/AdminView/TeamsAdmin';
import AdminActivity from '../pages/AdminView/AdminActivity';
import AdminDiagnostics from '../pages/AdminView/AdminDiagnostics';
import FeedbackTriage from '../pages/FeedbackTriage/FeedbackTriage';
import AccountView from '../pages/AccountView/AccountView';
import EpisodesListView from '../pages/AccountView/EpisodesListView';
import EpisodeDetailView from '../pages/AccountView/EpisodeDetailView';
import RiderList from '../pages/RiderList/RiderList';
import RiderView from '../pages/RiderView/RiderView';
import { Navigate } from 'react-router-dom';
import SessionConfigs from '../pages/SessionView/SessionDetails/SessionConfigs/SessionConfigs';
import SessionCharts from '../pages/SessionView/SessionDetails/SessionCharts/SessionCharts';
import TyrePlots from '../pages/SessionView/SessionDetails/SessionCharts/TyrePlots';
import SessionBreakdown from '../pages/SessionView/SessionDetails/SessionBreakdown/SessionBreakdown';
import RequireFullSession from '../pages/SessionView/SessionDetails/RequireFullSession';
import RunHealthCheck from 'pages/RunView/RunHealthCheck/RunHealthCheck';
import RouteSelection from '../pages/RouteSelection/RouteSelection';
import GateFromRunPicker from '../pages/RouteSelection/GateFromRunPicker';
import RoutePicker from '../pages/RouteSelection/RoutePicker';
import SessionPicker from '../pages/SessionList/SessionPicker';
import RouteManagement from '../pages/RouteManagement/RouteManagement';
import TyreReports from '../pages/TyreReports/TyreReports';
import MultiGate from '../pages/MultiGate/MultiGate';
import Statistics from '../pages/Statistics/Statistics';
import ModelsView from '../pages/ModelsView/ModelsView';
import DesignSystem from '../pages/DesignSystem/DesignSystem';
import { RUN_TABS } from '../pages/RunView/runTabs';
import { isAdmin } from '../utils/userRole';
import { lazy, Suspense, ReactNode } from 'react';

const RunMap3D = lazy(() => import('pages/RunView/RunMap3D/RunMap3D'));
const RunDiagnostic = lazy(() => import('pages/RunView/RunDiagnostic/RunDiagnostic'));
const RunLapParsing = lazy(() => import('pages/RunView/RunLapParsing/RunLapParsing'));
const RunHighRate = lazy(() => import('pages/RunView/RunHighRate/RunHighRate'));

// Elements for each RunView sub-route, keyed by the canonical path in RUN_TABS.
// The tab set (paths, labels, order, visibility) lives in one place — runTabs.ts —
// and this map only supplies the component each path renders (incl. lazy wrappers).
const RUN_TAB_ELEMENTS: Record<string, ReactNode> = {
  overview: <RunOverview />,
  plotter: <RunPlotter />,
  'aux-sensor': <RunAuxSensor />,
  'high-rate': (
    <Suspense
      fallback={
        <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
          <div className="cr-text-gray-500">Loading High Rate...</div>
        </div>
      }
    >
      <RunHighRate />
    </Suspense>
  ),
  'health-check': <RunHealthCheck />,
  '3d-map': (
    <Suspense
      fallback={
        <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
          <div className="cr-text-gray-500">Loading 3D Map...</div>
        </div>
      }
    >
      <RunMap3D />
    </Suspense>
  ),
  'lap-parsing': (
    <Suspense
      fallback={
        <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
          <div className="cr-text-gray-500">Loading Lap Parsing...</div>
        </div>
      }
    >
      <RunLapParsing />
    </Suspense>
  ),
};

export const SubRoutes = () => {
  const RunRoutes = RUN_TABS.map((tab) => ({
    path: tab.path,
    label: tab.label,
    element: RUN_TAB_ELEMENTS[tab.path],
  }));

  return [
    {
      path: '/',
      element: <Navigate to="/sessions" />,
    },
    {
      path: '/sessions',
      label: 'Sessions',
      icon: <StreamlinesSession />,
      element: <SessionList />,
    },
    {
      path: '/sessions/:sessionId',
      label: 'Session',
      element: <SessionView />,
      nestedRoutes: [
        {
          path: '',
          element: <Navigate to="config" replace />,
        },
        {
          path: 'config',
          label: 'Configs',
          element: <SessionConfigs />,
        },
        {
          path: 'results',
          label: 'Calculations',
          element: <RequireFullSession><SessionCalculatedData /></RequireFullSession>,
        },
        {
          path: 'charts',
          label: 'Charts',
          element: <RequireFullSession><SessionCharts /></RequireFullSession>,
        },
        {
          path: 'tyre-plots',
          label: 'Tyre Plots',
          element: <RequireFullSession><TyrePlots /></RequireFullSession>,
        },
        {
          path: 'breakdown',
          label: 'Calculation',
          element: <RequireFullSession><SessionBreakdown /></RequireFullSession>,
        },
      ],
    },
    {
      path: '/runs',
      label: 'Runs',
      icon: <StreamlinesRun />,
      element: <RunList />,
    },
    {
      path: '/runs/:runId',
      label: 'Run Details',
      element: <RunView />,
      nestedRoutes: RunRoutes,
    },
    {
      path: '/sessions/:sessionId/runs/:runId',
      label: 'Run Details',
      element: <RunView />,
      nestedRoutes: RunRoutes,
    },
    {
      path: '/runs/:runId/diagnostic',
      label: 'Run Diagnostic',
      element: (
        <Suspense fallback={<div className="cr-flex cr-h-64 cr-items-center cr-justify-center"><div className="cr-text-gray-500">Loading...</div></div>}>
          <RunDiagnostic />
        </Suspense>
      ),
    },
    {
      path: '/sessions/:sessionId/runs/:runId/diagnostic',
      label: 'Run Diagnostic',
      element: (
        <Suspense fallback={<div className="cr-flex cr-h-64 cr-items-center cr-justify-center"><div className="cr-text-gray-500">Loading...</div></div>}>
          <RunDiagnostic />
        </Suspense>
      ),
    },
    {
      path: '/equipment',
      label: 'Equipment',
      element: <EquipmentList />,
    },
    {
      path: '/tyre-reports',
      label: 'Tyre-Reports',
      element: <TyreReports />,
    },
    {
      path: '/multi-gate',
      label: 'Multi-gate',
      element: <MultiGate />,
    },
    {
      path: '/sessions/:sessionId/calibrations/:calibrationId',
      label: 'Calibrations',
      element: <CalibrationView />,
    },
    {
      path: '/calibrations/:calibrationId',
      label: 'Calibrations',
      element: <CalibrationView />,
    },
    {
      path: '/sessions/:sessionId/results',
      label: 'Session Calculated Data Results',
      element: <SessionCalculatedData />,
    },
    {
      path: '/team',
      label: 'Riders',
      icon: <StreamlinesRider />,
      element: <RiderList />,
    },
    {
      path: '/riders/:riderId',
      label: 'Rider',
      element: <RiderView />,
    },
    {
      path: '/advanced',
      label: 'Advanced',
      icon: <StreamlinesAdvanced />,
      submenu: [
        {
          path: '/equipment',
          label: 'Equipment',
          icon: <StreamlinesBicycle />,
        },
        {
          path: '/tyre-reports',
          label: 'Tyre-Reports',
          icon: <StreamlinesReports />,
        },
        {
          path: '/multi-gate',
          label: 'Multi-gate',
          icon: <StreamlinesRoute />,
        },
        {
          path: '/route-management',
          label: 'Routes',
          icon: <StreamlinesRoute />,
        },
        {
          path: '/statistics',
          label: 'Statistics',
          icon: <StreamlinesStatistics />,
        },
        {
          path: '/models',
          label: 'Models',
          icon: <StreamlinesAdvanced />,
        },
      ],
    },
    {
      path: '/route-management',
      label: 'Routes',
      element: <RouteManagement />,
    },
    {
      path: '/statistics',
      label: 'Statistics',
      element: <Statistics />,
    },
    {
      path: '/models',
      label: 'Models',
      element: <ModelsView />,
    },
    {
      path: '/account',
      label: 'Account',
      icon: <StreamlinesUser />,
      element: <AccountView />,
    },
    {
      // The team's own Nimbus recording files. Open to everyone — the server
      // holds each caller to their team — so there is no userFilter; the raw
      // table viewer inside the detail page is the part that stays admin-only.
      path: '/account/episodes',
      label: 'Recordings',
      element: <EpisodesListView />,
    },
    {
      path: '/account/episodes/detail/*',
      label: 'Recording',
      element: <EpisodeDetailView />,
    },
    {
      // Menu parent. MenuBar renders a route with a submenu as a toggle
      // button rather than a link, so /admin is only ever reached by a
      // bookmark or a typed URL — hence the redirect element.
      path: '/admin',
      label: 'Admin',
      icon: <StreamlinesAdmin />,
      element: <Navigate to="/admin/teams" replace />,
      userFilter: isAdmin,
      submenu: [
        { path: '/admin/teams', label: 'Teams' },
        { path: '/admin/feedback', label: 'Feedback' },
        { path: '/admin/activity', label: 'Activity' },
        { path: '/admin/diagnostics', label: 'Diagnostics' },
      ],
    },
    {
      path: '/admin/teams',
      label: 'Teams',
      element: <TeamsAdmin />,
      userFilter: isAdmin,
    },
    {
      path: '/admin/activity',
      label: 'Activity',
      element: <AdminActivity />,
      userFilter: isAdmin,
    },
    {
      path: '/admin/diagnostics',
      label: 'Diagnostics',
      element: <AdminDiagnostics />,
      userFilter: isAdmin,
    },
    {
      path: '/admin/design-system',
      label: 'Design System',
      element: <DesignSystem />,
      userFilter: isAdmin,
    },
    {
      path: '/admin/feedback',
      label: 'Feedback',
      element: <FeedbackTriage />,
      userFilter: isAdmin,
    },
    {
      // The episode DB viewer lived here until it moved under Account; this
      // keeps a bookmark of the old list working.
      path: '/admin/episodes',
      element: <Navigate to="/account/episodes" replace />,
    },
    {
      path: '/route-selection',
      label: 'Route Selection',
      element: <RouteSelection />,
    },
    {
      path: '/gate-from-run',
      label: 'Create Gate from Run',
      element: <GateFromRunPicker />,
    },
    {
      path: '/route-picker',
      label: 'Route Picker',
      element: <RoutePicker />,
    },
    {
      path: '/session-picker',
      label: 'Session Picker',
      element: <SessionPicker />,
    },
  ];
};
